import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  CalendarClock,
  History,
  Megaphone,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { Modal } from "../../../../components/ui/Modal";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { RelativeTime } from "../../../../components/ui/RelativeTime";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { showToast } from "../../../../utils/showToast";
import { apiErrorMessage, apiErrorStatus } from "../../../../utils/apiErrorMessage";
import {
  createAiNotice,
  deleteAiNotice,
  getAiNotices,
  isEndpointMissing,
  updateAiNotice,
} from "./aiNoticesApi";
import {
  NOTICE_AUDIENCES,
  NOTICE_TEXT_MAX,
  NOTICE_VIEWS,
  classifyNotice,
  defaultNoticeWindow,
  draftToBody,
  formatNoticeRange,
  groupNotices,
  normalizeNoticeResponse,
  normalizeNoticesResponse,
  noticeEditChanges,
  noticeToDraft,
  requiresAdmin,
  validateNoticeDraft,
} from "./aiNotices";
import {
  CharCounter,
  ChipRadioGroup,
  CONTROL_CLS,
  PRIMARY_BTN_CLS,
  SECONDARY_BTN_CLS,
  SectionTitle,
  TEXTAREA_CLS,
} from "./agentSections/primitives";

// Avisos operativos del asistente: hechos con fecha de inicio y término
// (feriados, cortes de servicio, cambios de horario) que la IA usa SOLO
// mientras están vigentes. Son por courier y los ven todos sus agentes.
//
// A diferencia de la memoria, la lista completa llega en un único GET y las
// tres vistas (vigentes, próximos, vencidos) se calculan en el cliente con
// groupNotices: el estado de un aviso cambia con el paso del tiempo, así que
// cambiar de pestaña no vuelve a pedir nada al backend. `nowMs` se refresca
// cada minuto para que un aviso pase de "Próximo" a "Vigente" sin recargar.
//
// `canManage=false` deja la lista en solo lectura sin esconderla: el rol puede
// ver los avisos, pero no cambiarlos. El backend revalida cada escritura.

// Relectura del reloj: un minuto alcanza para que la clasificación no quede
// vieja a la vista, sin re-renderizar la lista en cada segundo.
const CLOCK_REFRESH_MS = 60000;

// Botón de acción del ítem: alto táctil en móvil (h-11) y compacto desde md.
const ITEM_BTN_CLS = "h-11 md:h-8";

// Orden de presentación: el mismo de NOTICE_AUDIENCES (Comercios, Clientes,
// Repartidores, Todos).
const NOTICE_AUDIENCE_LABELS = {
  merchant: "Comercios",
  customer: "Clientes",
  driver: "Repartidores",
  all: "Todos",
};

const EMPTY_STATES = {
  active: {
    Icon: Megaphone,
    title: "No hay avisos vigentes",
    description:
      "Crea uno para un feriado, un corte de servicio o un cambio de horario. La IA lo usa solo mientras está vigente.",
  },
  upcoming: {
    Icon: CalendarClock,
    title: "No hay avisos programados",
    description:
      "Programa los feriados con anticipación: el aviso se activa solo en la fecha indicada.",
  },
  expired: {
    Icon: History,
    title: "No hay avisos vencidos en los últimos 7 días",
    description: "Los avisos vencidos se muestran una semana y después se ocultan.",
  },
};

/**
 * Texto del error de una acción, de más específico a más genérico. Un 404/405
 * sin texto útil es la ruta aún no desplegada; un 4xx con mensaje del backend
 * explica mejor que el genérico; un 5xx queda fuera (su texto es interno).
 * @param {unknown} err
 * @param {(key: string) => string} t
 * @param {string} fallback clave i18n de la acción que falló
 * @returns {string}
 */
function actionErrorMessage(err, t, fallback) {
  const status = apiErrorStatus(err);
  const serverMessage = status !== null && status < 500 ? apiErrorMessage(err, "") : "";
  if (isEndpointMissing(err) && !serverMessage) {
    return t("Los avisos operativos todavía no están disponibles.");
  }
  if (serverMessage) return serverMessage;
  return t(fallback);
}

/** Ids para aria-describedby, sin los vacíos (un id "false" no existe). */
const describedBy = (...ids) => ids.filter(Boolean).join(" ");

function NoticesSkeleton() {
  return (
    <div
      className="divide-y divide-border rounded-lg border border-border bg-bg"
      aria-hidden="true"
    >
      {[0, 1, 2].map((i) => (
        <div key={i} className="p-4 space-y-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

/**
 * Formulario de alta y edición de un aviso. Vive a nivel de módulo (no dentro
 * del render): definirlo adentro remontaría el subtree en cada tecla y mataría
 * el foco.
 *
 * @param {object} props
 * @param {object|null} props.initial - borrador inicial; null para alta
 * @param {(draft: object) => Promise<void>} props.onSubmit - maneja sus propios
 *   errores (toast); acá solo se evita el doble envío
 * @param {() => void} props.onCancel
 * @param {string} props.submitLabel
 * @param {boolean} props.canManageAdminOnly - tier de administrador: el único
 *   que puede publicar "sin operación" para todas las audiencias
 */
function NoticeForm({ initial, onSubmit, onCancel, submitLabel, canManageAdminOnly }) {
  const { t } = useTranslation();
  // Ids únicos por instancia: puede haber un formulario de alta y uno de
  // edición montados a la vez.
  const uid = useId();
  const textId = `notice-text-${uid}`;
  const startsId = `notice-starts-${uid}`;
  const endsId = `notice-ends-${uid}`;
  const audiencesId = `notice-audiences-${uid}`;
  const noOperationId = `notice-noop-${uid}`;
  const noOperationHelpId = `${noOperationId}-help`;
  const windowHintId = `notice-window-hint-${uid}`;

  // Las fechas llegan como valores de <input type="datetime-local"> con la
  // hora de pared de Chile (ver aiNotices.js).
  const [text, setText] = useState(initial?.text || "");
  const [startsAt, setStartsAt] = useState(initial?.startsAt || "");
  const [endsAt, setEndsAt] = useState(initial?.endsAt || "");
  const [audiences, setAudiences] = useState(() =>
    Array.isArray(initial?.audiences) ? initial.audiences : [],
  );
  const [noOperation, setNoOperation] = useState(initial?.noOperation === true);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Abrir el formulario (alta o edición) lleva el foco al primer campo: el
  // botón que lo abrió desaparece (edición) o queda lejos (alta), y sin esto el
  // foco cae al <body>.
  const textRef = useRef(null);
  useEffect(() => {
    textRef.current?.focus();
  }, []);

  // Editar un campo retira su error: el mensaje describía el valor anterior y
  // dejarlo hasta el próximo envío contradice lo que el admin ya corrigió. La
  // validación completa se repite al guardar.
  const clearError = (...keys) =>
    setErrors((prev) => {
      if (!keys.some((key) => prev[key])) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });

  const toggleAudience = (id) => {
    // Las audiencias también deciden si el aviso exige el tier de administrador.
    clearError("audiences", "noOperation");
    setAudiences((prev) => {
      if (prev.includes(id)) return prev.filter((value) => value !== id);
      // "Todos" es exclusiva: marcar otra audiencia la desmarca, y viceversa.
      return id === "all" ? ["all"] : [...prev.filter((value) => value !== "all"), id];
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const draft = { text, startsAt, endsAt, audiences, noOperation };
    const nextErrors = validateNoticeDraft(draft);
    // Mismo criterio que el backend (requiredWriteAction): avisarlo acá evita
    // un 403 después de llenar el formulario.
    if (!canManageAdminOnly && requiresAdmin(draft)) {
      nextErrors.noOperation =
        "Solo un administrador puede publicar un aviso sin operación para todas las audiencias. Elige audiencias específicas.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    try {
      await onSubmit(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-3" onSubmit={handleSubmit} noValidate>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <label htmlFor={textId} className="hd-field-label block text-xs font-medium text-fg">
            {t("Aviso")}
          </label>
          <CharCounter value={text.length} max={NOTICE_TEXT_MAX} />
        </div>
        <textarea
          ref={textRef}
          id={textId}
          rows={3}
          value={text}
          maxLength={NOTICE_TEXT_MAX}
          onChange={(event) => {
            clearError("text");
            setText(event.target.value);
          }}
          aria-invalid={!!errors.text}
          aria-describedby={errors.text ? `${textId}-error` : undefined}
          // Mismo criterio que ui/Input: el campo inválido se marca en rojo.
          className={cn(TEXTAREA_CLS, errors.text && "!border-status-error")}
        />
        {errors.text ? (
          <p
            id={`${textId}-error`}
            className="text-[12px] md:text-[11px] text-status-error"
            role="alert"
          >
            {t(errors.text)}
          </p>
        ) : (
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t(
              "Escribe lo que la IA debe saber, en una o dos frases. Ejemplo: El lunes 2 de noviembre no hay retiros ni entregas por feriado.",
            )}
          </p>
        )}
      </div>

      {/* fieldset: "Desde" y "Hasta" se anuncian como parte de la vigencia, y
          la aclaración de zona horaria viaja con cada campo (sin ella, un admin
          fuera de Chile leería la hora como local). */}
      <fieldset className="space-y-1">
        <legend className="hd-field-label block text-xs font-medium text-fg">
          {t("Vigencia")}
        </legend>
        {/* min-w-0 en la celda y en el control: el datetime-local nativo de
            Chrome móvil trae un ancho mínimo propio que, sin esto, desborda la
            columna del grid. */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 min-w-0">
            <label htmlFor={startsId} className="hd-field-label block text-xs font-medium text-fg">
              {t("Desde")}
            </label>
            <input
              id={startsId}
              type="datetime-local"
              value={startsAt}
              onChange={(event) => {
                // El término se valida contra el inicio: cambiar uno invalida
                // el error del otro.
                clearError("startsAt", "endsAt");
                setStartsAt(event.target.value);
              }}
              aria-invalid={!!errors.startsAt}
              aria-describedby={describedBy(errors.startsAt && `${startsId}-error`, windowHintId)}
              className={cn(CONTROL_CLS, "min-w-0", errors.startsAt && "!border-status-error")}
            />
            {errors.startsAt && (
              <p
                id={`${startsId}-error`}
                className="text-[12px] md:text-[11px] text-status-error"
                role="alert"
              >
                {t(errors.startsAt)}
              </p>
            )}
          </div>
          <div className="space-y-1 min-w-0">
            <label htmlFor={endsId} className="hd-field-label block text-xs font-medium text-fg">
              {t("Hasta")}
            </label>
            <input
              id={endsId}
              type="datetime-local"
              value={endsAt}
              onChange={(event) => {
                clearError("startsAt", "endsAt");
                setEndsAt(event.target.value);
              }}
              aria-invalid={!!errors.endsAt}
              aria-describedby={describedBy(errors.endsAt && `${endsId}-error`, windowHintId)}
              className={cn(CONTROL_CLS, "min-w-0", errors.endsAt && "!border-status-error")}
            />
            {errors.endsAt && (
              <p
                id={`${endsId}-error`}
                className="text-[12px] md:text-[11px] text-status-error"
                role="alert"
              >
                {t(errors.endsAt)}
              </p>
            )}
          </div>
        </div>
        <p id={windowHintId} className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Hora de Chile. Un aviso dura como máximo 31 días.")}
        </p>
      </fieldset>

      <fieldset
        className="space-y-2"
        aria-describedby={errors.audiences ? `${audiencesId}-error` : undefined}
      >
        <legend className="hd-field-label block text-xs font-medium text-fg">
          {t("A quién aplica")}
        </legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {NOTICE_AUDIENCES.map((id) => (
            <label key={id} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={audiences.includes(id)}
                onChange={() => toggleAudience(id)}
                className="!w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
              />
              <span className="text-xs text-fg">{t(NOTICE_AUDIENCE_LABELS[id])}</span>
            </label>
          ))}
        </div>
        {errors.audiences && (
          <p
            id={`${audiencesId}-error`}
            className="text-[12px] md:text-[11px] text-status-error"
            role="alert"
          >
            {t(errors.audiences)}
          </p>
        )}
      </fieldset>

      <div className="space-y-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            id={noOperationId}
            type="checkbox"
            checked={noOperation}
            onChange={(event) => {
              clearError("noOperation");
              setNoOperation(event.target.checked);
            }}
            aria-invalid={!!errors.noOperation}
            aria-describedby={describedBy(
              errors.noOperation && `${noOperationId}-error`,
              noOperationHelpId,
            )}
            className="!w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
          />
          <span className="text-xs text-fg">
            {t("Sin operación (feriado / día sin servicio)")}
          </span>
        </label>
        <p id={noOperationHelpId} className="text-[12px] md:text-[11px] text-fg-muted">
          {t(
            "Mientras esté vigente, la IA responderá que ese día no se opera en vez de buscar quién retira.",
          )}
        </p>
        {errors.noOperation && (
          <p
            id={`${noOperationId}-error`}
            className="text-[12px] md:text-[11px] text-status-error"
            role="alert"
          >
            {t(errors.noOperation)}
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="submit" className={cn(PRIMARY_BTN_CLS, "w-full sm:w-auto")} disabled={saving}>
          {submitLabel}
        </button>
        <button
          type="button"
          className={cn(SECONDARY_BTN_CLS, "w-full sm:w-auto")}
          onClick={onCancel}
          disabled={saving}
        >
          {t("Cancelar")}
        </button>
      </div>
    </form>
  );
}

/**
 * Un aviso de la lista. Componente de módulo (ver NoticeForm).
 */
function NoticeItem({
  notice,
  view,
  canManage,
  canManageAdminOnly,
  busy,
  editing,
  onEdit,
  onCancelEdit,
  onSubmitEdit,
  onRequestDelete,
}) {
  const { t, i18n } = useTranslation();

  if (editing) {
    return (
      <li className="p-4 space-y-2">
        <NoticeForm
          initial={noticeToDraft(notice)}
          onSubmit={onSubmitEdit}
          onCancel={onCancelEdit}
          submitLabel={t("Guardar cambios")}
          canManageAdminOnly={canManageAdminOnly}
        />
      </li>
    );
  }

  const lockedForRole = !canManageAdminOnly && requiresAdmin(notice);

  const meta =
    view === "active"
      ? { label: t("Termina"), value: notice.endsAt }
      : view === "upcoming"
        ? { label: t("Empieza"), value: notice.startsAt }
        : { label: t("Venció"), value: notice.endsAt };

  return (
    <li className="p-4 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-fg tabular-nums">
          <CalendarClock className="w-3.5 h-3.5 text-fg-muted" aria-hidden="true" />
          {formatNoticeRange(notice, i18n?.language)}
        </span>
        <div className="flex flex-wrap gap-1">
          {notice.noOperation && (
            <Badge tone="warning" dot>
              {t("Sin operación")}
            </Badge>
          )}
          {notice.audiences.map((id) => (
            <Badge key={id} tone="neutral">
              {NOTICE_AUDIENCE_LABELS[id] ? t(NOTICE_AUDIENCE_LABELS[id]) : id}
            </Badge>
          ))}
        </div>
      </div>

      <p className="text-sm text-fg whitespace-pre-wrap break-words">{notice.text}</p>

      <p className="text-[12px] md:text-[11px] text-fg-muted">
        {meta.label} <RelativeTime value={meta.value} t={t} />
      </p>

      {/* Un aviso "sin operación" para todos solo lo cambia el tier de
          administrador (el backend exige admin_only para editarlo y borrarlo). */}
      {canManage && lockedForRole && (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Solo un administrador puede cambiar este aviso.")}
        </p>
      )}

      {canManage && !lockedForRole && (
        <div className="flex flex-wrap gap-2 pt-1">
          {view !== "expired" && (
            <Button
              size="sm"
              variant="outline"
              className={ITEM_BTN_CLS}
              iconLeft={<Pencil className="w-3.5 h-3.5" aria-hidden="true" />}
              onClick={() => onEdit(notice.id)}
              disabled={busy}
            >
              {t("Editar")}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className={cn(ITEM_BTN_CLS, "text-status-error")}
            iconLeft={<Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
            onClick={() => onRequestDelete(notice)}
            disabled={busy}
          >
            {t("Borrar")}
          </Button>
        </div>
      )}
    </li>
  );
}

export default function AiNoticesTab({ canManage = true, canManageAdminOnly = true }) {
  const { t } = useTranslation();

  // loading → consultando; ready → hay respuesta (aunque venga vacía);
  // missing → la ruta no está desplegada (404/405); error → falló de verdad.
  const [view, setView] = useState(NOTICE_VIEWS[0]);
  const [state, setState] = useState("loading");
  const [notices, setNotices] = useState([]);
  // Instante con el que se clasifican las tres vistas. Vive en estado (y no se
  // lee Date.now() en el render) para que el tick programado re-renderice.
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Acciones en vuelo por aviso ({ [id]: acción }): un mapa y no un solo slot,
  // para que borrar uno no rehabilite los botones de otro mientras su request
  // sigue en vuelo.
  const [busy, setBusy] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [error, setError] = useState(null);

  // Descarta respuestas de una carga vieja (una recarga posterior a una acción
  // puede solaparse con la inicial).
  const requestIdRef = useRef(0);
  // StrictMode monta → limpia → monta sobre el MISMO fiber: el flag se re-fija
  // dentro del efecto, no solo al declarar el ref.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // `t` cambia de identidad al cambiar el idioma: si entrara a las deps del
  // load, traducir la pantalla volvería a consultar el endpoint. Los textos de
  // error usan la instancia vigente al momento del fallo.
  const tRef = useRef(t);
  tRef.current = t;

  // Estado vigente para el catch de una recarga silenciosa (que no puede leer
  // el `state` de su closure: sería el del render en que se creó).
  const stateRef = useRef(state);
  stateRef.current = state;

  // Con un formulario abierto el reloj se congela: si el aviso que se edita
  // cambiara de vista por el paso del tiempo, su fila (y el borrador que tiene
  // adentro) se desmontaría a mitad de la edición.
  const formOpen = Boolean(editingId) || adding;
  const formOpenRef = useRef(formOpen);
  formOpenRef.current = formOpen;
  const tickClock = useCallback(() => {
    if (!formOpenRef.current) setNowMs(Date.now());
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const isCurrent = () => aliveRef.current && requestIdRef.current === requestId;

    // Silenciosa = recarga tras una acción: la lista no vuelve al esqueleto ni
    // borra el error que se esté mostrando.
    if (!silent) {
      setState("loading");
      setError(null);
    }

    try {
      const res = await getAiNotices();
      if (!isCurrent()) return;
      setNotices(normalizeNoticesResponse(res));
      // El reloj también se refresca al cargar (salvo con un formulario
      // abierto, ver tickClock): la clasificación usa la hora del momento.
      tickClock();
      setError(null);
      setState("ready");
    } catch (err) {
      if (!isCurrent()) return;
      // Una recarga silenciosa fallida no debe borrar una lista que ya se
      // mostraba. Sin lista (la carga inicial seguía en vuelo y esta recarga la
      // reemplazó, o ya había un error) cae al mismo camino que una carga
      // normal: si no, quedaría el esqueleto para siempre o un error sin texto.
      if (silent && stateRef.current === "ready") return;
      if (isEndpointMissing(err)) {
        setState("missing");
      } else {
        // Mismo criterio que las acciones: un 4xx con texto del backend se
        // muestra; un 5xx o un fallo de red, el genérico (su texto es interno).
        const status = apiErrorStatus(err);
        const serverMessage = status !== null && status < 500 ? apiErrorMessage(err, "") : "";
        setError(serverMessage || tRef.current("No se pudieron cargar los avisos operativos."));
        setState("error");
      }
    }
  }, [tickClock]);

  useEffect(() => {
    load();
  }, [load]);

  // Un aviso pasa de "Próximo" a "Vigente" (y de "Vigente" a "Vencido") con el
  // paso del tiempo: el tick re-clasifica sin volver a pedir la lista.
  useEffect(() => {
    const id = window.setInterval(tickClock, CLOCK_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [tickClock]);

  // Al cerrarse el formulario el reloj se pone al día de inmediato.
  useEffect(() => {
    if (!formOpen) setNowMs(Date.now());
  }, [formOpen]);

  // Toda recarga posterior a una acción llama a la `load` VIGENTE.
  const loadRef = useRef(load);
  loadRef.current = load;
  const reloadSilently = useCallback(() => loadRef.current({ silent: true }), []);

  const markBusy = useCallback((id, action) => {
    setBusy((prev) => ({ ...prev, [id]: action }));
  }, []);
  const clearBusy = useCallback((id) => {
    setBusy((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  // Errores de acciones: toast con el texto más específico disponible y
  // recarga silenciosa cuando la lista quedó vieja: un 404 CON mensaje (otro
  // admin borró el aviso) o un 409 (lo cambió mientras esta pestaña estaba
  // abierta).
  const reportActionError = useCallback(
    async (err, fallback) => {
      showToast(actionErrorMessage(err, tRef.current, fallback), "error");
      const status = apiErrorStatus(err);
      if (status === 409 || (status === 404 && apiErrorMessage(err, ""))) {
        await reloadSilently();
      }
    },
    [reloadSilently],
  );

  const handleCreate = useCallback(
    async (draft) => {
      const body = draftToBody(draft);
      try {
        const res = await createAiNotice(body);
        showToast(tRef.current("Aviso guardado."), "success");
        setAdding(false);
        // Llevar al admin a la vista donde cae el aviso nuevo. Si la respuesta
        // no trae el aviso, se clasifica con las fechas que se acaban de
        // enviar (el body ya trae instantes ISO).
        const created = normalizeNoticeResponse(res);
        const target = created
          ? classifyNotice(created, Date.now())
          : classifyNotice(
              { startMs: Date.parse(body.starts_at), endMs: Date.parse(body.ends_at) },
              Date.now(),
            );
        setView(target);
        await reloadSilently();
      } catch (err) {
        await reportActionError(err, "No se pudo guardar el aviso.");
      }
    },
    [reloadSilently, reportActionError],
  );

  const handleSubmitEdit = useCallback(
    async (draft) => {
      if (!editingId) return;
      const original = notices.find((notice) => notice.id === editingId);
      // Solo viaja lo que cambió (ver noticeEditChanges). Guardar sin tocar
      // nada no escribe: el backend respondería 400 a un PATCH vacío.
      const body = original ? noticeEditChanges(original, draft) : draftToBody(draft);
      if (Object.keys(body).length === 0) {
        setEditingId(null);
        return;
      }
      try {
        const res = await updateAiNotice(editingId, body);
        setEditingId(null);
        showToast(tRef.current("Aviso actualizado."), "success");
        // Igual que en el alta: si la nueva vigencia saca el aviso de esta
        // vista, se lleva al admin a la vista donde quedó.
        const updated = normalizeNoticeResponse(res);
        if (updated) setView(classifyNotice(updated, Date.now()));
        await reloadSilently();
      } catch (err) {
        // El formulario queda abierto para corregir lo que el backend rechazó.
        await reportActionError(err, "No se pudo guardar el aviso.");
      }
    },
    [editingId, notices, reloadSilently, reportActionError],
  );

  const handleConfirmDelete = useCallback(async () => {
    const target = pendingDelete;
    setPendingDelete(null);
    if (!target) return;

    markBusy(target.id, "delete");
    try {
      await deleteAiNotice(target.id);
      showToast(tRef.current("Aviso borrado."), "success");
      await reloadSilently();
    } catch (err) {
      await reportActionError(err, "No se pudo borrar el aviso.");
    } finally {
      clearBusy(target.id);
    }
  }, [clearBusy, markBusy, pendingDelete, reloadSilently, reportActionError]);

  const handleViewChange = useCallback((next) => {
    // Los formularios abiertos pertenecen a la vista anterior: cambiar de
    // pestaña no puede dejar un editor huérfano.
    setEditingId(null);
    setAdding(false);
    setView(next);
  }, []);

  // Las tres vistas se calculan en el cliente sobre la única lista recibida.
  const grouped = useMemo(() => groupNotices(notices, nowMs), [notices, nowMs]);
  const current = grouped[view] || [];

  // Etiqueta + contador en una sola línea: en 390px un chip partido en tres
  // renglones se lee como tres controles. Si no caben, el grupo se desliza en
  // horizontal, igual que la fila de pestañas del kit (ui/Tabs) en móvil.
  const viewOptions = [
    { value: "active", text: `${t("Vigentes")} · ${grouped.active.length}` },
    { value: "upcoming", text: `${t("Próximos")} · ${grouped.upcoming.length}` },
    { value: "expired", text: `${t("Vencidos")} · ${grouped.expired.length}` },
  ].map(({ value, text }) => ({
    value,
    label: <span className="whitespace-nowrap">{text}</span>,
  }));

  const empty = EMPTY_STATES[view] || EMPTY_STATES.active;

  return (
    <section className="space-y-4" aria-label={t("Avisos operativos")}>
      <p className="text-xs text-fg-muted">
        {t(
          "Avisos con fecha de inicio y término que la IA tiene en cuenta mientras están vigentes: feriados, cortes de servicio o cambios de horario. Las horas son de Chile.",
        )}
      </p>

      {/* Anuncio contextual para lectores de pantalla: un número suelto no dice
          de qué es. Vacío hasta que hay respuesta, para no anunciar un 0 que el
          backend todavía no dijo. */}
      <p role="status" aria-atomic="true" className="sr-only">
        {state === "ready"
          ? t("{{count}} avisos vigentes").replace("{{count}}", () =>
              String(grouped.active.length),
            )
          : ""}
      </p>

      {/* Sin la ruta desplegada no hay nada que filtrar ni que agregar:
          ofrecer controles que solo pueden volver a fallar es ruido. */}
      {state !== "missing" && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="max-w-full overflow-x-auto">
            <ChipRadioGroup
              value={view}
              options={viewOptions}
              onChange={handleViewChange}
              ariaLabel={t("Vigencia de los avisos")}
            />
          </div>

          {canManage && (
            <button
              type="button"
              onClick={() => setAdding((prev) => !prev)}
              aria-expanded={adding}
              className={cn(PRIMARY_BTN_CLS, "w-full sm:w-auto")}
            >
              <Plus className="w-3.5 h-3.5" aria-hidden="true" />
              {t("Nuevo aviso")}
            </button>
          )}
        </div>
      )}

      {!canManage && state !== "missing" && (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Tu rol puede ver los avisos, pero no cambiarlos.")}
        </p>
      )}

      {adding && state !== "missing" && (
        <div className="rounded-lg border border-border bg-bg p-4 space-y-3">
          <SectionTitle>{t("Nuevo aviso")}</SectionTitle>
          <NoticeForm
            initial={{ text: "", ...defaultNoticeWindow(Date.now()), audiences: ["all"], noOperation: false }}
            onSubmit={handleCreate}
            onCancel={() => setAdding(false)}
            submitLabel={t("Guardar aviso")}
            canManageAdminOnly={canManageAdminOnly}
          />
        </div>
      )}

      {state === "loading" && <NoticesSkeleton />}

      {state === "missing" && (
        <PendingBackendNotice>
          {t("Los avisos operativos todavía no están disponibles.")}
        </PendingBackendNotice>
      )}

      {state === "error" && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2"
          role="alert"
        >
          <span className="flex items-center gap-1.5 text-xs text-status-error min-w-0">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {error}
          </span>
          <button type="button" onClick={() => load()} className={SECONDARY_BTN_CLS}>
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
            {t("Reintentar")}
          </button>
        </div>
      )}

      {state === "ready" && current.length === 0 && (
        <EmptyState
          icon={<empty.Icon className="w-6 h-6" aria-hidden="true" />}
          title={t(empty.title)}
          description={t(empty.description)}
        />
      )}

      {state === "ready" && current.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border bg-bg">
          {current.map((notice) => (
            <NoticeItem
              key={notice.id}
              notice={notice}
              view={view}
              canManage={canManage}
              canManageAdminOnly={canManageAdminOnly}
              busy={Boolean(busy[notice.id])}
              editing={editingId === notice.id}
              onEdit={setEditingId}
              onCancelEdit={() => setEditingId(null)}
              onSubmitEdit={handleSubmitEdit}
              onRequestDelete={setPendingDelete}
            />
          ))}
        </ul>
      )}

      <Modal
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title={t("Borrar aviso")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete}>
              {t("Borrar")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">{t("Vas a borrar este aviso:")}</p>
        <p className="mt-1 text-sm font-medium text-fg line-clamp-3 break-words">
          {pendingDelete?.text}
        </p>
        <p className="mt-2 text-sm text-fg-muted">
          {t("La IA deja de usarlo de inmediato y no se puede deshacer.")}
        </p>
      </Modal>
    </section>
  );
}
